import { Spin, message } from "antd";
import React from "react";
import BaseForm from "../../components/Form/BaseForm";
import {
  APIError,
  FormFieldConfig,
  Opportunity,
  OpportunityProspect as OpportunityProspectInterface,
  SelectOptionsInterface,
} from "../../helpers/shared-interfaces";

import * as Sentry from "@sentry/browser";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import StepContainer from "../../components/StepContainer";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../../helpers/utilities";
import { ASSIGN_OPPORTUNITY_PROSPECT_TO_OPPORTUNITY } from "../../gql/opportunity";

interface Props {
  opportunityProspect?: string;
  opportunityProspects: OpportunityProspectInterface[];
  opportunity: Opportunity;
  handleOpportunityProspectChange: (opportunityType: string) => void;
  getNextViewName: () => string | null;
  getPreviousViewName: () => string | null;

  setPercentage: (percentage: number) => void;
  next: () => void;
  prev: () => void;
}

interface State {
  opportunityProspect: null | string;
  isLoading: boolean;
}

export function remodelOpportunityProspects(
  opportunityProspects: OpportunityProspectInterface[],
): SelectOptionsInterface[] {
  const remodeledArray: SelectOptionsInterface[] = [];
  for (const opportunityProspect of opportunityProspects) {
    remodeledArray.push({
      id: opportunityProspect.id,
      name: opportunityProspect.name,
    });
  }
  return remodeledArray;
}

export default class OpportunityProspect extends React.Component<Props, State> {
  reference = React.createRef<FormInstance>();

  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      opportunityProspect: null,
      isLoading: true,
    };

    this.assignOpportunityProspectToOpportunity =
      this.assignOpportunityProspectToOpportunity.bind(this);
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  componentDidMount(): void {
    this.props.setPercentage(20);
    this.setState({ isLoading: false });
  }

  assignOpportunityProspectToOpportunity<T>(
    opportunityProspect: string,
  ): Promise<AxiosResponse<T>> {
    const { opportunity } = this.props;
    return request({
      query: ASSIGN_OPPORTUNITY_PROSPECT_TO_OPPORTUNITY,
      variables: {
        opportunityId: opportunity.id,
        opportunityProspectId: opportunityProspect,
      },
    });
  }

  next = async (): Promise<void> => {
    interface Response {
      data?: {
        assignOpportunityProspectToOpportunity?: {
          opportunity?: Opportunity;
          errors: APIError[];
        };
      };
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields([
        "opportunityProspect",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    const assignMutation = this.assignOpportunityProspectToOpportunity;

    try {
      const response: AxiosResponse<Response> = await assignMutation<Response>(
        data.opportunityProspect,
      );

      const { err, handled } = responseHandler<Response>(
        response,
        "assignOpportunityProspectToOpportunity",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        this.props.next();
      }
    } catch (error) {
      this.setState({ isLoading: false });

      message.error(`Request Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  render(): JSX.Element {
    const {
      handleOpportunityProspectChange,
      opportunityProspects,
      opportunityProspect,
    } = this.props;
    const { isLoading } = this.state;

    const fields: FormFieldConfig[] = [
      {
        name: "opportunityProspect",
        inputType: "select",
        rule: "string",
        placeholder: "Opportunity Prospect",
        label: "Opportunity Prospect",
        required: true,
        colConfig: {
          span: 8,
        },
        initialValue: opportunityProspect,
        selectOptions: remodelOpportunityProspects(opportunityProspects),
        changeHandler: handleOpportunityProspectChange,
      },
    ];

    return (
      <Spin spinning={isLoading}>
        <StepContainer
          getNextViewName={this.props.getNextViewName}
          getPreviousViewName={this.props.getPreviousViewName}
          style={{
            position: "relative",
            top: "-10px",
          }}
          config={{
            prev: this.props.prev,
            next: this.next,
            showNext: true,
            showPrev: true,
          }}
        >
          <BaseForm
            layout="vertical"
            reference={this.reference}
            fieldsToHide={[]}
            fields={fields}
          ></BaseForm>
        </StepContainer>
      </Spin>
    );
  }
}
